import React, { useEffect, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { PVMUrls } from 'config/urls';
import { colorConstants } from 'config/colorConstants';
import _ from 'lodash';

const errorMessage: string = 'Something went wrong!';

interface Props {
    queryKey: any[];
    variables?: any;
    queryConfig?: {
        enabled?: boolean;
        cacheTime?: number;
    };
    offset: number;
}

interface outputType {
    data: any;
    loading: boolean;
    error: any;
}

function useFetchFeatureAttributeData({ queryKey, variables = {}, queryConfig = {}, offset }: Props): outputType {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //initializing states
            if (!offset) setData(null);
            setError(null);
            setLoading(true);

            try {
                const data = await axios({
                    method: 'post',
                    url: PVMUrls.FeatureAttributeDataUrl,
                    data: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
        //  enabled: false,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;

    //error handling
    if (!apiLoading && apiError && !error) {
        // if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            // if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            if (offset) {
                const newData = { ...data };
                newData.data = [...data.data, ...parseGroupedData(payload?.data)];
                setData(newData);
            } else {
                const parsedData: any = { minVal: payload.min_value, maxVal: payload.max_value };
                parsedData.data = parseGroupedData(payload?.data);
                parsedData.count = payload.total_count;
                setData(parsedData);
            }
        } catch (error) {
            onFetchError(error);
            console.error(errorMessage, error);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
    };
    return outputData;
}

export { useFetchFeatureAttributeData };

const parseGroupedData = (obj: any) => {
    const result: any[] = [];
    obj?.forEach((key: any) => {
        const item = {
            sales: key.data,
            labelSales: key.data,
            category: key.attribute_name,
            color: Number(key.data) < 0 ? colorConstants.red : colorConstants.limeGreen,
            disableHover: false,
        };
        result.push(item);
    });
    return result;
};
